import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-overrides.css";
import moment from "moment";
import { getBulkLists, sendBulkSMS, sendQuickSMS, updateSent } from "store/bulk-lists/actions";
import Swal from 'sweetalert2';
import { getContactLists } from "store/contacts-lists/actions";
// Import components
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import BulkSMSModal from "./BulkSMSModal";

// Utility to export data to CSV
const exportToCSV = (data, fileName) => {
  const csvContent =
    "data:text/csv;charset=utf-8," +
    [
      Object.keys(data[0]).join(","),
      ...data.map((row) => Object.values(row).join(",")),
    ].join("\n");

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const DatatableTables = () => {
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: 10,
    page: 0,
    sortBy: 'id',
    ascendingSort: false,
  });
  const [searchInput, setSearchInput] = useState("");
  const dispatch = useDispatch();
  const [initBulkData, setInitBulkData] = useState(null);
  const [quickSmsModal, setQuickSmsModal] = useState(false);
  const [bulkSmsModal, setBulkSmsModal] = useState(false);
  const [smsData, setSmsData] = useState({
    sender: "",
    msisdn: "",
    message: "",
    characterCount: 0,
  });

  const navigate = useNavigate();

  const handleDateFilter = async () => {
    setIsLoading(true); // Start loading
    try {
      // Dispatch the action (assuming it’s async but doesn’t return a Promise we can await)
      dispatch(
        getBulkLists({
          from: startDate,
          to: endDate,
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortBy: pagination.sortBy,
          ascendingSort: pagination.ascendingSort,
        })
      );

      // Simulate a delay to ensure the loading state is visible (optional)
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 1-second delay as a fallback
    } catch (error) {
      console.error("Error filtering data:", error);
    } finally {
      setIsLoading(false); // Stop loading regardless of success or failure
    }
  };

  const [bulkSmsData, setBulkSmsData] = useState({
    sender: "",
    send_date_time: "",
    contactList: [],
    message: "",
    characterCount: 0,
  });

  const toggleQuickSmsModal = () => setQuickSmsModal(!quickSmsModal);
  const toggleBulkSmsModal = () => setBulkSmsModal(!bulkSmsModal);

  const handleSmsChange = (e) => {
    const { name, value } = e.target;
    setSmsData((prevState) => ({
      ...prevState,
      [name]: value,
      characterCount: name === "message" ? value.length : prevState.characterCount,
    }));
  };

  const handleBulkSmsChange = (e) => {
    const { name, value } = e.target;
    if (name === "contactList") {
      const contactList = bulkSmsData?.contactList || [];
      const exists = contactList.includes(value);
      setBulkSmsData(
        {
          ...bulkSmsData,
          contactList: exists
            ? contactList.filter((item) => item !== value)
            : [...contactList, value],
        });
      return;
    }

    setBulkSmsData((prevState) => ({
      ...prevState,
      [name]: value,
      characterCount: name === "message" ? value.length : prevState.characterCount,
    }));
  };

  // react-datepicker hands back a Date; store it directly (moment() parses it
  // downstream the same as the old datetime-local string).
  const handleBulkDateChange = (date) => {
    setBulkSmsData((prevState) => ({ ...prevState, send_date_time: date }));
  };

  const handleFieldInsert = (field, target) => {
    if (target === "quick") {
      setSmsData((prevState) => ({
        ...prevState,
        message: `${prevState.message} ${field}`.trim(),
        characterCount: `${prevState.message} ${field}`.trim().length,
      }));
    } else {
      setBulkSmsData((prevState) => ({
        ...prevState,
        message: `${prevState.message} ${field}`.trim(),
        characterCount: `${prevState.message} ${field}`.trim().length,
      }));
    }
  };

  const onCopyToBulk = () => {
    setQuickSmsModal(false)
    setBulkSmsModal(true);
    setBulkSmsData(prevState => ({ ...prevState, message: smsData.message }))
  };

  const columns = useMemo(
    () => [
      {
        header: "From",
        accessorKey: "shortcode",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Message",
        accessorKey: "message",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (info) => (
          <div style={messageColumnStyle}>{info.getValue()}</div>
        ),
      },
      {
        header: "Date Sent",
        accessorKey: "send_date_time",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }) => {
          const date = moment(getValue());
          return date.format('DD MMM YYYY hh:mm A');
        },
      },
      {
        header: "Contacts",
        accessorKey: "send_count",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (info) => {
          const v = info.getValue();
          return v == null || v === "" ? v : Number(v).toLocaleString();
        },
      },
      {
        header: "Units",
        accessorKey: "unit_consumed",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (info) => {
          const v = info.getValue();
          return v == null || v === "" ? v : Number(v).toLocaleString();
        },
      },
      {
        header: "Lists",
        accessorKey: "lists",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const lists = info.getValue() || [];
          if (!lists.length) return "—";
          return (
            <div style={messageColumnStyle}>
              {lists.map((l) => l.name).filter(Boolean).join(", ")}
            </div>
          );
        },
      },
      {
        header: "Actions",
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-primary"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/message-detail/${cellProps.row.original.id}`, {
                  state: { campaign: cellProps.row.original },
                });
              }}
            >
              <i className="mdi mdi-eye font-size-18" />
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  const UserAccountProperties = createSelector(
    (state) => state.User,
    (user) => ({
      error: user.error,
      userProfile: user.userProfile
    })
  );

  const {
    error: accountError,
    userProfile
  } = useSelector(UserAccountProperties);

  const handleSendQuick = () => {
    if ((!userProfile?.Client?.remaining_units) || (smsData.characterCount > userProfile?.Client?.remaining_units)) {
      showNotification("Insuficient Units balance", "Your balance is insufficient please top up!", 'error')
    }
    if (!smsData.sender) {
      setSmsData({ ...smsData, sender: shortcodes?.[0]?.shortcode })
    }
    dispatch(
      sendQuickSMS({
        ...smsData,
        subject: smsData.subject || ' ',
        message: smsData.message,
        msisdn: smsData.msisdn,
        from: smsData.sender || shortcodes?.[0]?.shortcode
      }),
    );
  }

  const handleSendBulk = () => {
    if ((!userProfile?.Client?.remaining_units) || (bulkSmsData.characterCount > userProfile?.Client?.remaining_units)) {
      showNotification("Insuficient balance", "Your balance is insufficient", 'error')
    }
    const params = {
      ...bulkSmsData,
      short_code: bulkSmsData.sender || shortcodes?.[0]?.shortcode,
      gateway: 1,
      send_date_time: bulkSmsData.send_date_time
        ? moment(bulkSmsData.send_date_time).format('YYYY-MM-DD HH:mm')
        : moment().format('YYYY-MM-DD HH:mm'),
      message: bulkSmsData.message,
      status: 1,
      type: 1,
      subject: bulkSmsData.subject || ' ',
    };

    dispatch(
      sendBulkSMS({
        params,
        contact_list_id: Array.isArray(bulkSmsData.contactList)
          ? bulkSmsData.contactList
          : [bulkSmsData.contactList],
      }),
    );
  }

  const reports = [
    { title: "Total Units Used", iconClass: "bx-envelope", description: (userProfile?.Client?.total_used_units || 0).toLocaleString() },
    { title: "Units Remaining", iconClass: "bx-bar-chart-alt-2", description: (userProfile?.Client?.remaining_units || 0).toLocaleString() },
  ];

  // Meta title
  document.title = "OUTBOX | Sent Messages";

  useEffect(() => {
    handleDateFilter();
  }, [pagination, startDate, endDate, dispatch]);

  // Contact-list picker catalog — load all lists once (the Select filters
  // client-side). Must NOT reuse the bulk-table pagination (size 10), or lists
  // past the first page become unselectable.
  useEffect(() => {
    dispatch(getContactLists({ page: 0, pageSize: 200 }));
  }, [dispatch]);

  const CombinedProperties = createSelector(
    (state) => state.BulkList,
    (state) => state.ContactList,
    (state) => state.Config,
    (bulkList, contactList, config) => ({
      bulkError: bulkList.error,
      bulkList: bulkList.bulkLists,
      bulkTotal: bulkList.bulkTotal,
      bulkTotalPages: bulkList.bulkTotalPages,
      contactError: contactList.error,
      contactLists: contactList.contactLists,
      shortcodes: config.shortcodes,
      bulkSent: bulkList.bulkSent,
      quickSent: bulkList.quickSent
    })
  );

  const { bulkError, bulkList, bulkTotal, bulkTotalPages, contactError, contactLists, shortcodes, bulkSent, quickSent } = useSelector(CombinedProperties);

  // Client-side text filter over the loaded page (campaigns has no server keyword search).
  const filteredBulk = (bulkList || []).filter((r) => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return true;
    return (r.message || "").toLowerCase().includes(q) || (r.shortcode || "").toLowerCase().includes(q);
  });

  useEffect(() => {
    if (quickSent) {
      setQuickSmsModal(false);
      showNotification('Success', 'Message sent success', 'success',);
    }
    if (bulkSent) {
      setBulkSmsModal(false);
      showNotification('Success', 'Bulk Message schedulled successfully', 'success',);
    }
  }, [bulkSent, quickSent]);

  const onChangePagination = (
    { page, limit }, filters, sorter) => {
    setPagination({ ...pagination, page: page ?? pagination.page, pageSize: limit ?? pagination.pageSize })
  }

  const showNotification = (title, message, icon) => {
    Swal.fire({
      icon,
      title,
      text: message,
      confirmButtonText: 'OK',
    }).then(() => {
      dispatch(updateSent());
    })
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Sent Messages" breadcrumbItem="Outbox" />

        <Row>
          {(reports || []).map((report, key) => (
            <Col md="6" key={"_col_" + key}>
              <Card className="mini-stats-wid">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium">{report.title}</p>
                      <h4 className="mb-0">{report.description}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                      <span className="avatar-title rounded-circle bg-primary">
                        <i className={"bx " + report.iconClass + " font-size-24"}></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-end gap-2">
            <div className="me-3">
              <Label className="mb-1">Search</Label>
              <Input
                style={{ minWidth: 360 }}
                placeholder="Search messages…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1">From Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
              />
            </div>
            <div>
              <Label className="mb-1">To Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
            <Button color="primary" onClick={handleDateFilter} disabled={isLoading}>
              {isLoading ? (<><Spinner size="sm" /> Loading...</>) : "Filter"}
            </Button>
          </div>
          <div className="d-flex gap-2">
            <Button
              outline
              color="primary"
              disabled={!bulkList || bulkList.length === 0}
              onClick={() => exportToCSV(bulkList || [], "Sent_Messages")}
            >
              <i className="mdi mdi-download"></i> Export Data
            </Button>
            <Button color="success" onClick={toggleQuickSmsModal}>
              <i className="mdi mdi-plus"></i> Quick SMS
            </Button>
            <Button color="primary" onClick={toggleBulkSmsModal}>
              <i className="mdi mdi-plus"></i> Schedule Bulk SMS
            </Button>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filteredBulk}
          isGlobalFilter={false}
          isPagination={true}
          isCustomPageSize={true}
          pageSizeAtBottom={true}
          paginationData={pagination}
          onChangePagination={onChangePagination}
          manualPagination={true}
          pageCount={bulkTotalPages}
          totalRows={bulkTotal}
          onRowClick={(row) =>
            navigate(`/message-detail/${row.id}`, { state: { campaign: row } })
          }
          SearchPlaceholder="Search messages..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        {/* Quick SMS Modal */}
        <Modal isOpen={quickSmsModal} toggle={toggleQuickSmsModal} size="lg">
          <ModalHeader toggle={toggleQuickSmsModal}>Send a Quick Message</ModalHeader>
          <ModalBody className="p-4">
            <div className="mb-4">
              <Label for="sender" className="fw-semibold mb-2">* SEND FROM</Label>
              <Input
                type="select"
                name="sender"
                id="sender"
                defaultValue={shortcodes?.[0]?.shortcode}
                value={smsData.sender}
                onChange={handleSmsChange}
              >
                {shortcodes?.map((e) => (
                  <option key={e.shortcode} value={e.shortcode}>
                    {e.shortcode}
                  </option>
                ))}
              </Input>
            </div>

            <div className="mb-4">
              <Label for="msisdn" className="fw-semibold mb-2">* Send to MSISDN</Label>
              <Input
                type="text"
                name="msisdn"
                id="msisdn"
                placeholder="254 712 345 678"
                value={smsData.msisdn}
                onChange={handleSmsChange}
              />
            </div>

            <div className="mb-3">
              <Label className="fw-semibold mb-2">Salutation</Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<FIRSTNAME>", "quick")}>
                  &lt;FIRSTNAME&gt;
                </Button>
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<LASTNAME>", "quick")}>
                  &lt;LASTNAME&gt;
                </Button>
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<DATE>", "quick")}>
                  &lt;DATE&gt;
                </Button>
              </div>
              <Input
                type="textarea"
                name="message"
                value={smsData.message}
                onChange={handleSmsChange}
                rows={5}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center text-muted">
              <span>Characters: {smsData.characterCount}/160</span>
              <span>Messages: {Math.ceil(smsData.characterCount / 160) || 0}</span>
            </div>
          </ModalBody>
          <ModalFooter className="px-4 pb-4">
            <Button color="success" onClickCapture={handleSendQuick}>Quick Message &rarr;</Button>
            <Button color="warning" outline onClick={onCopyToBulk}>Copy to Bulk &rarr;</Button>
          </ModalFooter>
        </Modal>

        {/* Schedule Bulk SMS Modal */}
        <Modal isOpen={bulkSmsModal} toggle={toggleBulkSmsModal} size="lg">
          <ModalHeader toggle={toggleBulkSmsModal}>Schedule a Bulk Message</ModalHeader>
          <ModalBody className="p-4">
            <div className="mb-4">
              <Label for="sender" className="fw-semibold mb-2">* SEND FROM</Label>
              <Input
                type="select"
                name="sender"
                id="sender"
                defaultValue={shortcodes?.[0]?.shortcode}
                value={bulkSmsData.sender}
                onChange={handleBulkSmsChange}
              >
                {shortcodes?.map((e) => (
                  <option key={e.shortcode} value={e.shortcode}>
                    {e.shortcode}
                  </option>
                ))}
              </Input>
            </div>

            <div className="mb-4">
              <Label for="date" className="fw-semibold mb-2">* Send Date &amp; Time</Label>
              {/* Popover: opens on click, closes once a time is picked (5-min slots).
                  autoComplete off so the browser doesn't offer prior datetimes. */}
              <DatePicker
                id="send_date_time"
                selected={bulkSmsData.send_date_time ? new Date(bulkSmsData.send_date_time) : null}
                onChange={handleBulkDateChange}
                showTimeSelect
                timeIntervals={5}
                timeCaption="Time"
                minDate={new Date()}
                dateFormat="EEE, dd MMM yyyy h:mm aa"
                placeholderText="Select date & time"
                autoComplete="off"
                className="form-control"
                wrapperClassName="kengen-dtp-wrapper"
                popperClassName="kengen-dtp-popper"
                popperPlacement="bottom-start"
              />
            </div>

            <div className="mb-4">
              <Label for="contactList" className="fw-semibold mb-2">* Contact List</Label>
              <Select
                isMulti
                name="contactList"
                id="contactList"
                options={contactLists.map((e) => ({
                  value: e.id,
                  label: e.name,
                }))}
              value={bulkSmsData.contactList.map((id) => {
                const contact = contactLists.find((e) => e.id === id);
                return contact ? { value: id, label: contact.name } : null;
              }).filter(Boolean)}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions.map((option) => option.value);
                setBulkSmsData((prevState) => ({
                  ...prevState,
                  contactList: selectedValues,
                }));
              }}
              placeholder="Select Contact List"
              styles={{
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? '#6c757d' : '#f8f9fa',
                  color: state.isSelected ? '#fff' : '#212529',
                  ':hover': {
                    backgroundColor: '#4E3080',
                    color: '#fff',
                  },
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: '#6c757d',
                  color: '#fff',
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: '#fff',
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: '#fff',
                  ':hover': {
                    backgroundColor: '#5a6268',
                    color: '#fff',
                  },
                }),
              }}
              />
            </div>

            <div className="mb-3">
              <Label className="fw-semibold mb-2">Salutation</Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<FIRSTNAME>", "bulk")}>
                  &lt;FIRSTNAME&gt;
                </Button>
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<LASTNAME>", "bulk")}>
                  &lt;LASTNAME&gt;
                </Button>
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<DATE>", "bulk")}>
                  &lt;DATE&gt;
                </Button>
              </div>
              <Input
                type="textarea"
                name="message"
                placeholder="Enter message"
                value={bulkSmsData.message}
                onChange={handleBulkSmsChange}
                rows={5}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center text-muted">
              <span>Characters: {bulkSmsData.characterCount}/160</span>
              <span>Messages: {Math.ceil(bulkSmsData.characterCount / 160) || 0}</span>
            </div>
          </ModalBody>

          <ModalFooter className="px-4 pb-4">
            <Button color="primary" onClickCapture={handleSendBulk}>Schedule Message</Button>
            <Button color="secondary" outline onClick={toggleBulkSmsModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

DatatableTables.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

const messageColumnStyle = {
  whiteSpace: "normal",
  wordWrap: "break-word",
  maxWidth: "400px",
};

export default DatatableTables;